// ---------- le compte du joueur, sans personne dedans ----------
// Le même système que Codigrid (son src/account.ts, et DEPLOY.md « Comptes et sauvegarde ») : le
// Worker fabrique un identifiant opaque (`POST /users`), l'appareil le garde et le renvoie en
// `Authorization: Bearer`. Pas d'inscription, pas d'e-mail, pas de mot de passe — un jeu qu'on
// ouvre pour une partie ne met pas un formulaire devant la première case.
//
// Créé PARESSEUSEMENT, à la première sauvegarde qui a besoin du serveur (src/sync.ts) ou quand le
// joueur ouvre son panneau pour lire son code : ouvrir le jeu une fois et le refermer ne crée rien
// nulle part.
//
// Ce que ça couvre : la progression survit à un cache vidé et passe de la version web à l'appli
// sur le même appareil. Elle survit à une DÉSINSTALLATION et à un changement de téléphone pour qui
// a noté son code de reprise (cinq mots, `GET /me/recovery`) et le retape ailleurs (`POST
// /recovery`, `adoptUser` plus bas). Sans ce code, le jeton part avec l'application.
//
// Les appels réseau du compte sont ICI et non dans src/api.ts : ils portent un jeton, ne se
// recopient jamais dans le cache hors ligne (une sauvegarde resservie d'hier écraserait celle
// d'aujourd'hui) et n'ont rien à voir avec le contenu que src/api.ts sert.

import { API } from './api';
import type { SaveState } from '../shared/save';

const KEY = 'verbigrid:user:v1';

/** Le serveur ne connaît plus ce jeton (compte supprimé, base refaite) : src/sync.ts en fabrique
 *  un neuf au lieu de réessayer un 401 pour toujours. */
export class Unauthorized extends Error {
  constructor() {
    super('Unauthorized');
  }
}

// Gardé comme la progression : un stockage plein ou refusé (navigation privée) ne doit jamais
// faire tomber le jeu. Jouer sans copie en ligne est une dégradation, un plantage non.
function readToken(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, token);
  } catch {
    /* pas de stockage : cette session se synchronise, la suivante repartira de zéro */
  }
}

let cached: string | null = readToken();
let minting: Promise<string | null> | null = null;

/** Le jeton si cet appareil en a déjà un, sans rien créer. */
export const currentUser = (): string | null => cached;

async function call(
  path: string,
  init: RequestInit = {},
  token?: string | null,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (init.body) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(API + path, { ...init, headers });
  if (res.status === 401) throw new Unauthorized();
  return res;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

/** UN compte, même si deux sauvegardes le demandent en même temps : sans ça, la seconde en
 *  fabriquerait un autre et la première progression partirait dans un compte orphelin. */
export function ensureUser(): Promise<string | null> {
  if (cached) return Promise.resolve(cached);
  minting ??= call('/users', { method: 'POST' })
    .then((r) => json<{ id: string }>(r))
    .then(({ id }) => {
      cached = id;
      writeToken(id);
      return id;
    })
    .catch(() => null) // hors ligne : la sauvegarde locale tient, le prochain essai créera
    .finally(() => {
      minting = null;
    });
  return minting;
}

/** « Supprimer mes données » : le compte et sa progression, sur le serveur (`DELETE /me`). Sans
 *  compte, il n'y a rien à supprimer là-bas — vrai aussi. Lève si le serveur ne répond pas : on
 *  ne fait alors RIEN côté appareil, pour ne pas laisser croire que c'est parti. */
export async function deleteAccount(): Promise<void> {
  if (!cached) return;
  const res = await call('/me', { method: 'DELETE' }, cached);
  if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
  forgetUser();
}

/** Oublier un jeton que le serveur refuse : la prochaine sauvegarde en fabriquera un neuf. */
export function forgetUser(): void {
  cached = null;
  writeToken(null);
}

/**
 * Cet appareil devient celui d'un compte existant : un code de reprise vient d'être échangé contre
 * son jeton. src/sync.ts (`resyncAll`) tire alors la progression du compte retrouvé et lui pousse
 * ce que cet appareil a joué de son côté — la fusion n'en perd rien (shared/save.ts).
 *
 * Le compte que cet appareil s'était fabriqué n'est PAS supprimé, comme dans Codigrid : c'est
 * peut-être le dernier endroit où vit une partie que ce téléphone a perdue, et un compte abandonné
 * ne coûte qu'une ligne.
 */
export function adoptUser(token: string): void {
  cached = token;
  writeToken(token);
}

export const fetchSave = async (token: string): Promise<SaveState> =>
  json(await call('/me/progress', {}, token));

/** Envoie des parties ENTIÈRES (jamais un delta) et rend leur fusion avec ce que le serveur avait. */
export const pushSave = async (token: string, save: SaveState): Promise<SaveState> =>
  json(await call('/me/progress', { method: 'PUT', body: JSON.stringify(save) }, token));

/** Le code de reprise de ce compte — créé au premier appel, le même ensuite. */
export async function fetchRecoveryCode(token: string): Promise<string> {
  const { code } = await json<{ code: string }>(await call('/me/recovery', {}, token));
  return code;
}

/**
 * Un code contre le compte qu'il désigne. `null` pour un code inconnu (404) : c'est une réponse
 * normale, à dire au joueur, pas une panne. `'busy'` pour le 429 de la limitation de débit — dix
 * essais par minute (backend/src/middleware/rateLimit.ts) : le joueur qui retape son code de
 * travers doit lire « réessaie dans une minute », pas « code inconnu ».
 */
export async function redeemRecoveryCode(code: string): Promise<string | null | 'busy'> {
  const res = await call('/recovery', { method: 'POST', body: JSON.stringify({ code }) });
  if (res.status === 404) return null;
  if (res.status === 429) return 'busy';
  return (await json<{ id: string }>(res)).id;
}

/**
 * Ce que le joueur tape, ramené à la forme stockée — la même fonction que `normalizeRecoveryCode`
 * du Worker (backend/src/utils/recovery.ts), recopiée ici parce que le jeu ne compile pas le
 * backend. Elle sert à AFFICHER le code retrouvé proprement ; le serveur renormalise de toute façon.
 */
export const normalizeCode = (v: string): string =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]+/g, '-')
    .replace(/^-+|-+$/g, '');
